import React, { useEffect, useState } from 'react';
import { cx } from '../cx';

// 사진 썸네일 한 장 — 고정 정사각 미리보기 + 삭제 버튼 (D-07 §3.2 #20 예외로 승인된 신규 컴포넌트, 승인 N-03).
// 표시 전용: 파일 읽기·URL 생성(objectURL 등)·해제는 소비처가 소유하고, 여기엔 src 만 넘긴다.
// onOpen 이 있으면 썸네일 전체가 버튼(원본 보기), onRemove 가 있으면 우상단 × 버튼. 두 버튼은 겹치지 않는 별개 요소.
// 상태: loading(스켈레톤) · error(src 없음·불러오기 실패 시 안내 글자). 색·크기는 토큰만 쓴다.
// contract 없는 손 작성 컴포넌트(Drawer.tsx와 동일 예외) — 클래스는 styles/components.css(.photo-thumb).
export type PhotoThumbSize = 'sm' | 'md' | 'lg';

export interface PhotoThumbProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onClick'> {
  /** 미리보기 이미지 URL */
  src?: string | null;
  /** 이미지 대체 텍스트 (장식이면 빈 문자열) */
  alt: string;
  /** 있으면 우상단 삭제 버튼 표시 */
  onRemove?: () => void;
  /** 삭제 버튼 스크린리더 이름. 기본 `alt 삭제` */
  removeLabel?: string;
  /** 있으면 썸네일 클릭(Enter·Space)으로 원본 보기 */
  onOpen?: () => void;
  /** 원본 보기 버튼 스크린리더 이름. 기본 `alt 크게 보기` */
  openLabel?: string;
  /** true 면 스켈레톤 */
  loading?: boolean;
  /** true 거나 src 가 비어 있으면 불러오기 실패 표시 */
  error?: boolean;
  /** 불러오기 실패 안내 글자. 기본 '불러오지 못함' */
  errorText?: string;
  /** 버튼 비활성(저장 중 등) */
  disabled?: boolean;
  /** sm 48 · md 64(기본) · lg 96 */
  size?: PhotoThumbSize;
}

export function PhotoThumb({
  src, alt, onRemove, removeLabel, onOpen, openLabel, loading = false, error = false, errorText = '불러오지 못함',
  disabled = false, size = 'md', className = '', ...props
}: PhotoThumbProps) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);

  const failed = !loading && (error || broken || !src);
  const body = loading ? (
    <span className="photo-thumb-skeleton" aria-hidden="true" />
  ) : failed ? (
    <span className="photo-thumb-error" role="img" aria-label={`${alt} ${errorText}`}>{errorText}</span>
  ) : (
    <img className="photo-thumb-img" src={src ?? undefined} alt={alt} onError={() => setBroken(true)} />
  );

  return (
    <div className={cx('photo-thumb', `photo-thumb-${size}`, className)} aria-busy={loading || undefined} {...props}>
      {onOpen && !loading && !failed ? (
        <button type="button" className="photo-thumb-open" aria-label={openLabel ?? `${alt} 크게 보기`} disabled={disabled} onClick={onOpen}>
          {body}
        </button>
      ) : body}
      {onRemove && (
        <button type="button" className="photo-thumb-remove" aria-label={removeLabel ?? `${alt} 삭제`} disabled={disabled} onClick={onRemove}>
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
}
